import { useMemo, useState } from "react";
import {
  RefreshCw, Loader2, ShoppingCart, Receipt,
  Award, Users, Tag, Package, Calendar, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { adminApi, formatCOP } from "../../lib/api";
import { toast } from "sonner";

import { PERIODS, SOURCES, SYNC_OPTIONS, fmtShort, fmtDate } from "./commercial/constants";
import { KpiCard, VariationBadge } from "./commercial/KpiCard";
import { MarketplaceTab } from "./commercial/MarketplaceTab";
import { VendedoresTab, VendedorDetail } from "./commercial/VendedoresTab";
import { MarcasTab, ProductosTab } from "./commercial/MarcasProductosTabs";
import { DigestCard } from "./commercial/DigestCard";
import { useCommercialData, useVendorDetail } from "./commercial/useCommercialData";

const TABS = [
  { v: "marketplace", l: "Marketplace", icon: ShoppingCart },
  { v: "vendedores", l: "Vendedores", icon: Users },
  { v: "marcas", l: "Marcas", icon: Tag },
  { v: "productos", l: "Productos", icon: Package },
];

export default function AdminCommercial() {
  const [period, setPeriod] = useState("month");
  const [source, setSource] = useState("all");
  const [sinIva, setSinIva] = useState(false);
  const [tab, setTab] = useState("marketplace");
  const [drillVendor, setDrillVendor] = useState(null);
  const [syncDays, setSyncDays] = useState(2);
  const [digestSending, setDigestSending] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const qs = useMemo(
    () => `period=${period}&source=${source}&sin_iva=${sinIva}`,
    [period, source, sinIva]
  );

  const {
    summary, timeline, vendedores, marcas, productos,
    syncStatus, digestLast, loading, refetch,
    setSyncStatus, setDigestLast,
  } = useCommercialData(qs);

  const vendorDetail = useVendorDetail(drillVendor, qs);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await adminApi.post(`/admin/commercial/sync?days_back=${syncDays}`);
      toast.success(`Sincronizando últimos ${syncDays} día${syncDays === 1 ? "" : "s"}...`);
      const poll = setInterval(async () => {
        const { data } = await adminApi.get(`/admin/commercial/sync-status`);
        setSyncStatus(data);
        if (!data.in_progress) {
          clearInterval(poll);
          setSyncing(false);
          toast.success(`Sincronización terminada · ${data.processed || 0} pedidos`);
          refetch();
        }
      }, 3000);
    } catch {
      setSyncing(false);
      toast.error("Error al iniciar sincronización");
    }
  };

  const handleSendDigest = async () => {
    setDigestSending(true);
    try {
      const { data } = await adminApi.post(`/admin/commercial/digest/send-now`);
      if (data.sent) {
        toast.success(`Resumen enviado a ${data.to}`);
      } else if (data.reason === "missing_api_key") {
        toast.error("Falta configurar RESEND_API_KEY en el servidor", { duration: 6000 });
      } else {
        toast.error(`No se pudo enviar: ${data.reason || data.error || "error"}`);
      }
      const h = await adminApi.get(`/admin/commercial/digest/history`);
      setDigestLast((h.data.items || [])[0] || null);
    } catch {
      toast.error("Error al enviar el resumen");
    } finally {
      setDigestSending(false);
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const handleBackfillNames = async () => {
    setBackfilling(true);
    try {
      const { data } = await adminApi.post(`/admin/backfill-cliente-nombres?limit=2000`);
      if (data.queued > 0) {
        toast.success(
          `Enriqueciendo ${data.queued} clientes en segundo plano. Recarga en unos minutos.`,
          { duration: 6000 }
        );
      } else {
        toast.info(`No hay nombres pendientes por resolver (${data.already_cached} ya en cache).`);
      }
    } catch (e) {
      toast.error(`Falló: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  const [backfillingIva, setBackfillingIva] = useState(false);
  const handleBackfillIva = async () => {
    setBackfillingIva(true);
    try {
      const { data } = await adminApi.post(`/admin/backfill-iva?limit=5000`);
      if (data.queued > 0) {
        toast.success(
          `Enriqueciendo IVA de ${data.queued} pedidos en segundo plano. Puede tomar varios minutos.`,
          { duration: 8000 }
        );
      } else {
        toast.info("Todos los pedidos ya tienen su vr_base cacheado.");
      }
    } catch (e) {
      toast.error(`Falló: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBackfillingIva(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-commercial-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Supervisión comercial</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-cm-yellow" />
            Dashboard Comercial
          </h1>
          <p className="text-sm text-cm-ink-soft mt-2 max-w-3xl">
            Vista consolidada de ventas, vendedores, marcas y productos · Portal + ERP Arcos.
            {syncStatus?.finished_at && (
              <span className="block text-xs mt-1">
                Última sincronización: {new Date(syncStatus.finished_at).toLocaleString("es-CO")} · {syncStatus.cached_count} pedidos en caché
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 min-w-[260px]" data-testid="sync-block">
          <div className="flex items-center gap-2 w-full">
            <Select
              value={String(syncDays)}
              onValueChange={(v) => setSyncDays(Number(v))}
              disabled={syncing || syncStatus?.in_progress}
            >
              <SelectTrigger className="h-9 text-xs border-cm-border bg-white w-[160px]" data-testid="sync-days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SYNC_OPTIONS.map((o) => (
                  <SelectItem key={o.v} value={String(o.v)} data-testid={`sync-days-${o.v}`}>
                    {o.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={syncing || syncStatus?.in_progress}
              className="border-cm-border h-9"
              data-testid="commercial-sync-btn"
            >
              {syncing || syncStatus?.in_progress ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sincronizando...</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" /> Sincronizar</>
              )}
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackfillNames}
            disabled={backfilling}
            className="text-xs text-cm-ink-soft hover:text-cm-red h-7"
            data-testid="backfill-nombres-btn"
            title="Resuelve los nombres de clientes que llegaron vacíos desde Arcos. Consulta Cliente/Get por cada uno y lo cachea. Puede tardar varios minutos."
          >
            {backfilling ? (
              <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Enriqueciendo...</>
            ) : (
              <>Enriquecer nombres de clientes</>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackfillIva}
            disabled={backfillingIva}
            className="text-xs text-cm-ink-soft hover:text-cm-red h-7"
            data-testid="backfill-iva-btn"
            title="Trae PedidoVrBase (subtotal sin IVA) de Arcos para los pedidos históricos. Necesario para que el toggle 'Sin IVA' muestre datos reales en vez de la aproximación /1.19."
          >
            {backfillingIva ? (
              <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Cargando IVA...</>
            ) : (
              <>Enriquecer subtotales (Sin IVA)</>
            )}
          </Button>
          {syncStatus?.in_progress && (
            <p className="text-[11px] text-cm-ink-soft" data-testid="sync-progress">
              Procesados: {syncStatus.processed || 0} pedidos · página {syncStatus.last_page || 0}
            </p>
          )}
        </div>
      </div>

      {/* Filters: period + source */}
      <Card className="bg-white border-cm-border shadow-none p-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-cm-ink-soft" />
            <span className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Período</span>
            <div className="flex gap-1" data-testid="period-pills">
              {PERIODS.map((p) => (
                <button
                  key={p.v}
                  type="button"
                  onClick={() => setPeriod(p.v)}
                  data-testid={`period-${p.v}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    period === p.v
                      ? "bg-cm-ink text-cm-cream"
                      : "bg-cm-cream-alt text-cm-ink-soft hover:text-cm-ink"
                  }`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>

          <div className="h-6 w-px bg-cm-border" />

          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Fuente</span>
            <div className="flex gap-1" data-testid="source-pills">
              {SOURCES.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setSource(s.v)}
                  data-testid={`source-${s.v}`}
                  title={s.desc}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    source === s.v
                      ? "bg-cm-red text-white"
                      : "bg-cm-cream-alt text-cm-ink-soft hover:text-cm-ink"
                  }`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          <div className="h-6 w-px bg-cm-border" />

          <div className="flex items-center gap-2" title="Los valores se calculan con el subtotal (PedidoVrBase) de Arcos, sin IVA discriminado.">
            <span className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">IVA</span>
            <button
              type="button"
              onClick={() => setSinIva(false)}
              data-testid="iva-toggle-con"
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !sinIva
                  ? "bg-cm-ink text-white"
                  : "bg-cm-cream-alt text-cm-ink-soft hover:text-cm-ink"
              }`}
            >
              Con IVA
            </button>
            <button
              type="button"
              onClick={() => setSinIva(true)}
              data-testid="iva-toggle-sin"
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sinIva
                  ? "bg-cm-ink text-white"
                  : "bg-cm-cream-alt text-cm-ink-soft hover:text-cm-ink"
              }`}
            >
              Sin IVA
            </button>
          </div>

          {summary && (
            <div className="ml-auto text-xs text-cm-ink-soft font-mono" data-testid="period-range">
              {summary.date_from} → {summary.date_to}
              {sinIva && <span className="ml-2 text-cm-red font-semibold">· SIN IVA</span>}
            </div>
          )}
        </div>
      </Card>

      {/* KPIs */}
      {loading && !summary ? (
        <Card className="p-12 text-center bg-white border-cm-border shadow-none">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
        </Card>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={Receipt}
              label="Ventas netas"
              value={formatCOP(summary.total)}
              sub={
                summary.nc_deducted > 0 ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] text-cm-ink-soft">
                      Bruto {formatCOP(summary.total_bruto)} · NC −{formatCOP(summary.nc_deducted)} ({summary.nc_count})
                    </span>
                    <VariationBadge pct={summary.variation_pct} />
                  </div>
                ) : (
                  <VariationBadge pct={summary.variation_pct} />
                )
              }
              tone="dark"
              testid="kpi-total"
            />
            <KpiCard
              icon={ShoppingCart}
              label="Pedidos"
              value={summary.count}
              sub={`${summary.breakdown.portal.count} portal · ${summary.breakdown.presencial.count} presencial`}
              testid="kpi-count"
            />
            <KpiCard
              icon={Award}
              label="Ticket promedio"
              value={formatCOP(summary.avg_ticket)}
              sub={summary.prev_total ? `Antes: ${formatCOP(summary.prev_total / Math.max(summary.prev_count || 1, 1))}` : "Sin comparativo"}
              testid="kpi-avg"
            />
            <KpiCard
              icon={Sparkles}
              label="% portal"
              value={summary.breakdown.all.total
                ? `${((summary.breakdown.portal.total / summary.breakdown.all.total) * 100).toFixed(1)}%`
                : "0%"}
              sub={`${formatCOP(summary.breakdown.portal.total)} del marketplace`}
              testid="kpi-portal-share"
            />
          </div>

          {/* Timeline chart */}
          <Card className="bg-white border-cm-border shadow-none p-5" data-testid="timeline-chart">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-lg">Evolución diaria</h2>
              <span className="text-xs text-cm-ink-soft">
                {timeline.length} día{timeline.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline.map((d) => ({ ...d, dayLabel: fmtDate(d.day) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFE9DC" />
                  <XAxis dataKey="dayLabel" tick={{ fontSize: 11 }} stroke="#8C8478" />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} stroke="#8C8478" />
                  <Tooltip
                    formatter={(v) => formatCOP(v)}
                    labelStyle={{ color: "#1A1A1A", fontWeight: 600 }}
                    contentStyle={{ backgroundColor: "white", border: "1px solid #EFE9DC", borderRadius: 6 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="portal" name="Portal" stackId="a" fill="#E30613" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="presencial" name="Presencial" stackId="a" fill="#FFC72C" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Tabs */}
          <div className="flex border-b border-cm-border" data-testid="commercial-tabs">
            {TABS.map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => { setTab(t.v); setDrillVendor(null); }}
                data-testid={`tab-${t.v}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  tab === t.v
                    ? "border-cm-red text-cm-ink"
                    : "border-transparent text-cm-ink-soft hover:text-cm-ink"
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.l}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === "marketplace" && <MarketplaceTab summary={summary} />}
          {tab === "vendedores" && (
            drillVendor && vendorDetail
              ? <VendedorDetail data={vendorDetail} onBack={() => setDrillVendor(null)} />
              : <VendedoresTab items={vendedores} onDrill={setDrillVendor} />
          )}
          {tab === "marcas" && <MarcasTab items={marcas} total={summary.total} />}
          {tab === "productos" && <ProductosTab items={productos} />}

          {/* Weekly digest */}
          <DigestCard
            last={digestLast}
            sending={digestSending}
            onSend={handleSendDigest}
          />
        </>
      ) : null}
    </div>
  );
}
